import { CartItem } from '../../../contexts/CartContext';
import { CouponData } from '../hooks/useCheckout';

interface Props {
  items: CartItem[];
  totalPrice: number;
  shippingCost: number;
  codFee: number;
  discount: number;
  grandTotal: number;
  freeShipping: boolean;
  couponCode: string;
  coupon: CouponData | null;
  couponError: string;
  couponLoading: boolean;
  onCouponChange: (v: string) => void;
  onApplyCoupon: () => void;
  onRemoveCoupon: () => void;
}

export default function OrderSummary({
  items, totalPrice, shippingCost, codFee, discount, grandTotal, freeShipping,
  couponCode, coupon, couponError, couponLoading,
  onCouponChange, onApplyCoupon, onRemoveCoupon
}: Props) {
  return (
    <div className="bg-white rounded-2xl p-6 sticky top-24">
      <h3 className="font-serif text-lg font-semibold text-coffee mb-5">Sipariş Özeti</h3>

      <div className="space-y-3 mb-5 max-h-52 overflow-y-auto pr-1">
        {items.map(item => (
          <div key={item.id} className="flex items-center gap-3">
            <div className="w-12 h-12 bg-cream rounded-lg overflow-hidden flex-shrink-0">
              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-coffee truncate">{item.name}</p>
              <p className="text-xs text-coffee-light">x{item.quantity}</p>
            </div>
            <span className="text-sm font-semibold text-coffee whitespace-nowrap">
              {(item.price * item.quantity).toLocaleString('tr-TR')}₺
            </span>
          </div>
        ))}
      </div>

      {/* Coupon */}
      {coupon ? (
        <div className="flex items-center gap-2 mb-5 p-3 bg-green-50 border border-green-200 rounded-xl">
          <i className="ri-coupon-3-line text-green-600 text-base" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-green-700">{coupon.kampanya_adi}</p>
            <p className="text-xs text-green-600">{coupon.kupon_kodu}</p>
          </div>
          <button onClick={onRemoveCoupon} className="w-5 h-5 flex items-center justify-center text-green-600 hover:text-red-500 transition-colors cursor-pointer">
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      ) : (
        <div className="mb-5">
          <div className="flex gap-2">
            <input
              type="text"
              value={couponCode}
              onChange={e => onCouponChange(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && onApplyCoupon()}
              placeholder="Kupon kodu"
              className="flex-1 px-3 py-2 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none text-coffee uppercase"
            />
            <button
              onClick={onApplyCoupon}
              disabled={couponLoading || !couponCode.trim()}
              className="px-4 py-2 bg-coffee text-white text-sm font-medium rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center gap-1"
            >
              {couponLoading ? <i className="ri-loader-4-line animate-spin" /> : 'Uygula'}
            </button>
          </div>
          {couponError && <p className="text-xs text-red-500 mt-1.5">{couponError}</p>}
        </div>
      )}

      {/* Totals */}
      <div className="space-y-2.5 border-t border-cream-dark pt-4">
        <div className="flex justify-between text-sm text-coffee-medium">
          <span>Ara Toplam</span>
          <span>{totalPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺</span>
        </div>
        <div className="flex justify-between text-sm text-coffee-medium">
          <span>Kargo</span>
          <span>
            {freeShipping
              ? <span className="text-green-600 font-medium">Ücretsiz</span>
              : `${shippingCost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺`}
          </span>
        </div>
        {codFee > 0 && (
          <div className="flex justify-between text-sm text-coffee-medium">
            <span>Kapıda Ödeme Ücreti</span>
            <span>+{codFee}₺</span>
          </div>
        )}
        {discount > 0 && (
          <div className="flex justify-between text-sm text-green-600 font-medium">
            <span>Kupon İndirimi</span>
            <span>-{discount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺</span>
          </div>
        )}
        <div className="flex justify-between text-base font-bold text-coffee pt-2 border-t border-cream-dark">
          <span>Genel Toplam</span>
          <span>{grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺</span>
        </div>
      </div>
    </div>
  );
}
